import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Image as ImageIcon, X } from 'lucide-react';
import BaseForm from './BaseForm';

const EventForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  submitting,
  isEdit = false
}) => {
  const [previewUrl, setPreviewUrl] = useState(() => {
    return formData.imageUrl || '';
  });

  useEffect(() => {
    if (!formData.imageUrl) {
      setPreviewUrl('');
      return;
    }

    if (formData.imageUrl.startsWith('data:')) {
      setPreviewUrl(formData.imageUrl);
      return;
    }

    setPreviewUrl(formData.imageUrl);
  }, [formData.imageUrl]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        e.target.value = '';
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('Image size should be less than 5MB');
        e.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setFormData(prev => ({
          ...prev,
          imageFile: file,
          imageUrl: reader.result
        }));
      };
      reader.onerror = () => {
        console.error('Error reading file');
        toast.error('Error reading file. Please try another image.');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setPreviewUrl('');
    setFormData(prev => ({
      ...prev,
      imageFile: null,
      imageUrl: ''
    }));
    const fileInput = document.getElementById('imageFile');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.title?.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!formData.description || formData.description.length < 10) {
      toast.error('Description must be at least 10 characters long');
      return false;
    }
    if (!formData.type) {
      toast.error('Please select an event type');
      return false;
    }
    if (!formData.startDate) {
      toast.error('Start date is required');
      return false;
    }
    if (!formData.endDate) {
      toast.error('End date is required');
      return false;
    }
    if (!formData.location?.trim()) {
      toast.error('Location is required');
      return false;
    }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error('End date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(e);
    }
  };

  return (
    <BaseForm
      title={isEdit ? 'Edit Event' : 'Add Event'}
      description="Add or update event details"
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitting={submitting}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-1">Event Title *</label>
          <Input
            id="title"
            value={formData.title || ''}
            onChange={handleChange}
            placeholder="Event title"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium mb-1">Start Date *</label>
            <Input
              id="startDate"
              type="datetime-local"
              value={formData.startDate || ''}
              onChange={handleChange}
              required
            />
          </div>
          
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium mb-1">End Date *</label>
            <Input
              id="endDate"
              type="datetime-local"
              value={formData.endDate || ''}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium mb-1">Location *</label>
          <Input
            id="location"
            value={formData.location || ''}
            onChange={handleChange}
            placeholder="Event location"
            required
          />
        </div>

        <div>
          <label htmlFor="type" className="block text-sm font-medium mb-1">Event Type *</label>
          <Select
            value={formData.type || ''}
            onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Select event type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Visit">Campus Visit</SelectItem>
              <SelectItem value="Conference">Conference</SelectItem>
              <SelectItem value="Seminar">Seminar</SelectItem>
              <SelectItem value="Workshop">Workshop</SelectItem>
              <SelectItem value="Webinar">Webinar</SelectItem>
              <SelectItem value="Delegation">Delegation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium mb-1">Description *</label>
          <Textarea
            id="description"
            minLength={10}
            maxLength={2000}
            value={formData.description || ''}
            onChange={handleChange}
            placeholder="Detailed event description"
            rows={4}
            required
          />
        </div>

        <div>
          <label htmlFor="registrationUrl" className="block text-sm font-medium mb-1">Registration URL</label>
          <Input
            id="registrationUrl"
            type="url"
            value={formData.registrationUrl || ''}
            onChange={handleChange}
            placeholder="https://example.com/register"
            pattern="https?://.+"
          />
          <p className="mt-1 text-xs text-gray-500">Must be a valid URL starting with http:// or https://</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Event Image</label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              {previewUrl ? (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-64 mx-auto rounded-md"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="mt-2 text-xs text-green-600">Image loaded - Click X to remove</div>
                </div>
              ) : (
                <>
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="imageFile"
                      className="relative cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 px-3 py-2 border border-indigo-600 rounded-md hover:bg-indigo-50"
                    >
                      <span>Upload a file</span>
                      <input
                        id="imageFile"
                        name="imageFile"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  {isEdit && !previewUrl && !formData.imageUrl && (
                    <div className="mt-2 text-xs text-orange-600 font-semibold">No image - upload one to add</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseForm>
  );
};

export default EventForm;